import { reducerWithInitialState } from 'typescript-fsa-reducers';
import { EmployeeStore } from '../../../external/AgencyApi';
import { AgencyDetailInfo } from '../../../external/AgencyDetailApi';
import { StoreInfo } from '../../../external/StoresApi';
import { actions } from '../../modules/Actions';

export interface AgencyInfoKey {
  /**
   * agencyApiのstoreCdをセットします
   *
   * このプロパティをstoreCdとすると、全体への影響が大きいため、
   * 既存のプロパティ名を踏襲し、agencyCdとしています
   */
  agencyCd: string;
}
export interface AgencyInfo extends AgencyInfoKey, EmployeeStore {
  /**
   * 選択済みフラグ (画面専用 APIのレスポンスには存在しない)
   */
  selectedForFilter?: boolean;
  /**
   * index (画面専用 APIのレスポンスには存在しない)
   * ※存在意義が不明だが、現行の実装に合わせる
   */
  index?: string;
}
export interface OriginalStoreInfo extends StoreInfo {
  index: number;
}

export type StoreAndAgencyInfo = StoreInfo | AgencyInfo | AgencyDetailInfo;
export interface StoreSelectorState {
  modalSelectedStores: string[];
  sortedModalSelectedStores: string[];
  selectedStores: string[];
  stores?: { [key: string]: StoreInfo };
  agencies?: { [key: string]: AgencyInfo };
  agencyDetail?: AgencyDetailInfo;
  isModalOpen: boolean;
  targetStores?: string[];
  groupAgencies?: {
    [key: string]: AgencyInfo[];
  };
  originalAgencies?: { [key: string]: AgencyInfo };
  originalStores?: OriginalStoreInfo[];
}

export type SelectedStoresArray = string[];

export const initialState: StoreSelectorState = {
  modalSelectedStores: [],
  selectedStores: [],
  isModalOpen: false,
  targetStores: [],
  sortedModalSelectedStores: [],
};

export const isStoreInfo = (store: StoreAndAgencyInfo): store is StoreInfo => store && 'ordcstmCd' in store;

/**
 * AgencyInfo型であるかどうか
 * 以下2つのプロパティの存在を持って、AgencyInfo型であると判定します
 * companyCfc
 * isStoreInCharge
 * @param store
 * @returns
 */
export const isAgencyInfo = (store: StoreAndAgencyInfo): store is AgencyInfo =>
  store && 'companyCfc' in store && 'isStoreInCharge' in store;

export const StoreSelectorReducer = reducerWithInitialState(initialState)
  .case(actions.storeSelector.initialize.store, (state: StoreSelectorState, payload: StoreInfo[]) => ({
    ...state,
    stores: convertStoreAndAgencyListToMap(payload.map((store) => ({ ...store, selectedForFilter: false }))),
    originalStores: payload.map((store, idx) => ({ ...store, selectedForFilter: false, index: idx + 1 })),
  }))
  .case(actions.storeSelector.initialize.agency, (state: StoreSelectorState, payload: AgencyInfo[]) => ({
    ...state,
    agencies: convertStoreAndAgencyListToMap(
      payload.map((agency, index) => ({ ...agency, index: index.toString(), selectedForFilter: true }))
    ),
    modalSelectedStores: payload.map((store) => store.agencyCd),
    targetStores: payload.map((store) => store.agencyCd),
    selectedStores: payload.flatMap((x) => x.agencyCdList),
    originalAgencies: convertAgencyListToObject(
      payload.map((agency, index) => ({ ...agency, index: index.toString(), selectedForFilter: true }))
    ),
    groupAgencies: groupByAgencyCd(payload.map((agency, index) => ({ ...agency, index: index.toString() }))),
  }))
  .case(actions.storeSelector.initialize.agencyDetail, (state: StoreSelectorState, payload: AgencyDetailInfo) => ({
    ...state,
    agencyDetail: { ...payload, selectedForFilter: true },
    modalSelectedStores: [payload.agencyCd],
    targetStores: [payload.agencyCd],
    selectedStores: [payload.agencyCd],
  }))
  .case(actions.storeSelector.toggleStore, (state: StoreSelectorState, payload: StoreAndAgencyInfo) => {
    const result = { ...state };

    const isSelected = !payload.selectedForFilter;

    let agencyDetail: AgencyDetailInfo;

    if (isStoreInfo(payload)) {
      result.stores = {
        ...state.stores,
        [payload.agencyCd]: { ...payload, selectedForFilter: isSelected },
      };
    } else if (isAgencyInfo(payload)) {
      result.agencies = {
        ...state.agencies,
        [payload.agencyCd]: { ...payload, selectedForFilter: isSelected },
      };
    } else {
      result.agencyDetail = { ...payload, selectedForFilter: isSelected };
      agencyDetail = result.agencyDetail;
    }
    result.selectedStores = [];

    result.modalSelectedStores
      .map((agencyCd) => result.stores?.[agencyCd] || result.agencies?.[agencyCd] || agencyDetail)
      .forEach((aa) => {
        if (isAgencyInfo(aa) && aa.selectedForFilter) {
          aa.agencyCdList.forEach((agencyCd) => result.selectedStores.push(agencyCd));
        } else {
          aa.selectedForFilter && result.selectedStores.push(aa.agencyCd);
        }
      });

    return result;
  })
  .case(actions.storeSelector.updateSelectedStores, (state: StoreSelectorState, selectedStoresPayload: SelectedStoresArray) => {
    const result = { ...state, modalSelectedStores: selectedStoresPayload };
    if (result.stores) {
      result.stores = convertStoreAndAgencyListToMap(
        Object.values(result.stores).map((store) => ({
          ...store,
          selectedForFilter: selectedStoresPayload.includes(store.agencyCd),
        }))
      );
      if (result.originalStores) {
        result.sortedModalSelectedStores = result.originalStores
          .filter((v) => selectedStoresPayload.includes(v.agencyCd))
          .sort((a, b) => a.index - b.index)
          .map((v) => v.agencyCd);
      }
    } else if (result.agencies) {
      result.agencies = convertStoreAndAgencyListToMap(
        Object.values(result.agencies).map((agency) => ({
          ...agency,
          selectedForFilter: selectedStoresPayload.includes(agency.agencyCd),
        }))
      );
    } else if (result.agencyDetail) {
      result.agencyDetail = convertStoreAndAgencyListToMap(
        Object.values(result.agencyDetail).map((agencyDetail) => ({
          ...agencyDetail,
          selectedForFilter: selectedStoresPayload.includes(agencyDetail.agencyCd),
        }))
      );
    }
    result.selectedStores = [];

    result.modalSelectedStores
      .map((agencyCd) => result.stores?.[agencyCd] || result.agencies?.[agencyCd] || result.agencyDetail)
      .forEach((aa) => {
        if (aa) {
          if (isAgencyInfo(aa) && aa.selectedForFilter) {
            aa.agencyCdList.forEach((agencyCd) => result.selectedStores.push(agencyCd));
          } else {
            aa.selectedForFilter && result.selectedStores.push(aa.agencyCd);
          }
        }
      });

    return result;
  })
  .case(actions.storeSelector.updateLocalStores, (state: StoreSelectorState, selectedStoresPayload: SelectedStoresArray) => {
    return {
      ...state,
      targetStores: selectedStoresPayload,
    };
  })
  .case(actions.storeSelector.toggleModal, (state: StoreSelectorState) => {
    return {
      ...state,
      isModalOpen: !state.isModalOpen,
    };
  })
  .case(actions.storeSelector.clearSelectedStores, (state: StoreSelectorState) => {
    if (!state.agencies) {
      return state;
    }
    const result = { ...state, selectedStores: [] };
    for (const agencyCd of Object.keys(state.agencies)) {
      state.agencies[agencyCd] = { ...state.agencies[agencyCd], selectedForFilter: false };
    }
    return result;
  })
  .case(actions.storeSelector.selectAllStores, (state: StoreSelectorState) => {
    if (!state.agencies) {
      return state;
    }
    const allStores: string[] = [];
    state.selectedStores = [];
    for (const agencyCd of Object.keys(state.agencies)) {
      state.agencies[agencyCd] = { ...state.agencies[agencyCd], selectedForFilter: true };
    }
    for (const agencyInfo of Object.values(state.agencies)) {
      // [不具合対応]
      // モーダルからの絞り込みにより、ドロワーに表示されている店舗が参照可能店舗よりも少ない場合、
      // 画面に表示していない店舗情報はパラメータに含まないようにする
      if (state.modalSelectedStores.includes(agencyInfo.agencyCd)) {
        agencyInfo.agencyCdList.forEach((agencyCd) => allStores.push(agencyCd));
      }
      // agencyInfo.agencyCdList.forEach((agencyCd) => allStores.push(agencyCd));
    }

    return { ...state, selectedStores: allStores };
  })
  .case(actions.storeSelector.selectOnlyStoresInCharge, (state: StoreSelectorState) => {
    if (!state.agencies) {
      return state;
    }
    const allStores: string[] = [];
    state.selectedStores = [];
    for (const agencyCd of Object.keys(state.agencies)) {
      if (state.agencies[agencyCd].isStoreInCharge) {
        state.agencies[agencyCd] = { ...state.agencies[agencyCd], selectedForFilter: true };
      } else {
        state.agencies[agencyCd] = { ...state.agencies[agencyCd], selectedForFilter: false };
      }
    }
    Object.values(state.agencies)
      .filter((v) => v.isStoreInCharge)
      // [不具合対応]
      // モーダルからの絞り込みにより、ドロワーに表示されている店舗が参照可能店舗よりも少ない場合、
      // 画面に表示していない店舗情報はパラメータに含まないようにする
      .filter((v) => state.modalSelectedStores.includes(v.agencyCd))
      .flatMap((agencyInfoList) => agencyInfoList.agencyCdList)
      .forEach((agencyCd) => {
        allStores.push(agencyCd);
      });
    return { ...state, selectedStores: allStores };
  });

const convertObjectListToMap = (list: any[], key: string) => {
  return list.reduce((accumulatedResult, array) => ({ ...accumulatedResult, [array[key]]: array }), {});
};

const convertStoreAndAgencyListToMap = (list: StoreAndAgencyInfo[]) => convertObjectListToMap(list, 'agencyCd');

const convertAgencyListToObject = (storeList: AgencyInfo[]) => {
  return storeList.reduce(
    (acc, store, index) => ({
      ...acc,
      [index]: store,
    }),
    {}
  );
};

const groupByAgencyCd = (storeList: AgencyInfo[]): Record<string, AgencyInfo[]> => {
  return storeList.reduce<Record<string, AgencyInfo[]>>((acc, store) => {
    const { agencyCd } = store;
    if (!acc[agencyCd]) {
      acc[agencyCd] = [];
    }
    acc[agencyCd].push(store);
    return acc;
  }, {});
};
